import React, { useState, useCallback, createContext, useContext, useMemo, type PropsWithChildren, type ComponentType } from 'react';

import { Modal } from './Modal';

export interface ModalContentProps<T> {
  readonly onConfirm: (value: T) => void;
  /**
   * Async method to execute within the modal before confirming
   */
  onValidate: (value: boolean) => Promise<void>;
  readonly onBack: () => void;
  readonly onClose: () => void;
}

interface ModalParams<T, P> {
  component: ComponentType<ModalContentProps<T> & P>;
  title?: string;
  props?: P;
}

interface ModalContextType {
  openModal: <T, P extends object>(params: ModalParams<T, P>) => Promise<T | null>;
}

interface ModalStackItem<T, P extends object> extends ModalParams<T, P> {
  resolve: (value: T | null) => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

export function ModalProvider({ children }: Readonly<PropsWithChildren>) {
  const [modalStack, setModalStack] = useState<ModalStackItem<any, any>[]>([]);

  const openModal = useCallback(<T, P extends object>({ title, component, props }: ModalParams<T, P>) => {
    return new Promise<T | null>(resolve => {
      const newModalItem: ModalStackItem<T, P> = {
        title,
        component,
        props,
        resolve,
      };

      setModalStack(stack => [...stack, newModalItem]);
    });
  }, []);

  const contextValue = useMemo<ModalContextType>(() => ({ openModal }), []);

  const handleAction = useCallback(
    (value: unknown) => {
      const topModal = modalStack.at(-1);
      if (topModal) {
        topModal.resolve(value);
        setModalStack(stack => stack.slice(0, -1));
      }
    },
    [modalStack]
  );

  const handleConfirm = useCallback((value: unknown) => handleAction(value), [handleAction]);
  const handleBack = useCallback(() => handleAction(null), [handleAction]);
  const handleClose = useCallback(() => {
    handleAction(null);
    setModalStack([]);
  }, [handleAction, setModalStack]);

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modalStack.map((modal, index) => {
        const isCurrentModal = modalStack.length - 1 === index;

        return (
          <Modal
            key={`${modal.title}-${modal.props?.id}`}
            title={modal.title}
            closeModal={handleClose}
            isHidden={!isCurrentModal}
            data-testid="modal"
            modal={{
              closeOnClickOutside: isCurrentModal,
            }}
          >
            <modal.component {...modal.props} onConfirm={handleConfirm} onBack={handleBack} onClose={handleClose} />
          </Modal>
        );
      })}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
